// src/stores/basespace.ts
import { defineStore } from 'pinia';
import apolloClient from '@/apollo/client';
import {
  BaseSpaceState,
  BsNode,
  BasespaceSelection,
  BasespaceTransferItem,
  BaseSpaceStateParameter,
  BaseSpaceStash,
} from '@/types/basespace-state';
import { BaseSpaceUtils } from '@/utils/basespace-utils';
import {
  BasespaceProjectsDocument,
  BasespaceProjectSamplesDocument,
  BasespaceSampleFilesDocument,
  BasespaceSampleType,
  BasespaceFileType,
  BasespaceTokenStatusType,
} from '@/gql/graphql';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';

export const useBaseSpaceStore = defineStore('basespace', {
  state: (): BaseSpaceState => ({
    tokenStatus: null, // null if not authenticated, or token status object
    nodes: new Map<string, BsNode>(), // id → BsNode
    projectsLoading: false, // true if projects are being loaded
    projectsLoaded: 0, // last loaded page for projects
    projectsTotal: undefined, // total projects reported by backend
  }),
  getters: {
    rootProjects: (s) =>
      [...s.nodes.values()].filter((n) => n.kind === 'project'),
    bsTransferItems: (self) => {
      // return BsNode[] of projects with nested samples and files marked for transfer (checked state)
      const items: BasespaceTransferItem[] = [];
      // iterate over all selected files and categorize them by sample and project
      self.nodes.forEach((file) => {
        if (file.checked && file.kind === 'file') {
          const sampleId = file.parentId;
          if (!sampleId) {
            throw new Error('File node without parent sample ID');
          }
          const sample = self.nodes.get(sampleId);
          if (!sample) {
            throw new Error('File node with non-existing parent sample ID');
          }
          const projectId = sample.parentId;
          if (!projectId) {
            throw new Error('Sample node without parent project ID');
          }
          const project = self.nodes.get(projectId);
          if (!project) {
            throw new Error('Sample node with non-existing parent project ID');
          }
          // add the file to the items list
          items.push({
            fileId: file.id,
            fileName: file.name,
            fileSize: (file.data as BasespaceFileType).size || '0',
            sampleId: sample.id,
            sampleName: sample.name,
            projectId: project.id,
            projectName: project.name,
          } as BasespaceTransferItem);
        }
      });
      // return the list of items to be transferred from Basespace
      return items;
    },
    bsSelection: (s) => {
      const projectIds: string[] = [];
      const sampleIds: string[] = [];
      const projects: string[] = [];
      const samples: string[] = [];
      const files: string[] = [];
      let totalSize = 0;
      s.nodes.forEach((node) => {
        // count the number of files, sample and projects
        if (node.checked && node.kind === 'file') {
          files.push(node.name);
          totalSize += Number((node.data as BasespaceFileType).size || 0);
          const sampleId = node.parentId;
          if (!sampleId) {
            throw new Error('File node without parent sample ID');
          }
          const sample = s.nodes.get(sampleId);
          if (!sample) {
            throw new Error('File node with non-existing parent sample ID');
          }
          const projectId = sample.parentId;
          if (!projectId) {
            throw new Error('Sample node without parent project ID');
          }
          const project = s.nodes.get(projectId);
          if (!project) {
            throw new Error('Sample node with non-existing parent project ID');
          }
          if (!projectIds.includes(projectId)) {
            projectIds.push(projectId);
            projects.push(project.name);
          }
          if (!sampleIds.includes(sampleId)) {
            sampleIds.push(sampleId);
            samples.push(sample.name);
          }
        }
      });
      return {
        projects: projects,
        samples: samples,
        files: files,
        totalSize: totalSize,
      } as BasespaceSelection;
    },
  },
  actions: {
    persistBsState() {
      sessionStorage.setItem(
        'bs_state_stash',
        JSON.stringify({
          nodes: Array.from(this.nodes.values()),
          projectsLoaded: this.projectsLoaded,
          projectsTotal: this.projectsTotal,
        } as BaseSpaceStash)
      );
    },
    recoverBsState() {
      const bsStateStash = sessionStorage.getItem('bs_state_stash');
      if (bsStateStash) {
        this.nodes.clear();
        // recover the state from the stash
        const { nodes, projectsLoaded, projectsTotal } = JSON.parse(
          bsStateStash
        ) as BaseSpaceStash;
        this.nodes = new Map(nodes.map((node) => [node.id, node]));
        this.projectsLoaded = projectsLoaded;
        this.projectsTotal = projectsTotal;
        sessionStorage.removeItem('bs_state_stash');
      }
    },
    baseSpaceConnect(bsServerPrefix: string, scope: string) {
      if (this.nodes.size > 0) {
        this.persistBsState();
      }

      const state = btoa(
        JSON.stringify({
          bsServerPrefix: `${bsServerPrefix}`,
          bsScope: `${scope}`,
        } as BaseSpaceStateParameter)
      );

      const authUrlWithParameters =
        BaseSpaceUtils.basespaceAuthUrl(bsServerPrefix) +
        `?client_id=${encodeURIComponent(
          BaseSpaceUtils.basespaceClientId(bsServerPrefix)
        )}` +
        `&redirect_uri=${encodeURIComponent(BaseSpaceUtils.redirectUri())}` +
        `&response_type=${encodeURIComponent('code')}` +
        `&state=${encodeURIComponent(state)}` +
        `&scope=${encodeURIComponent(scope)}`;

      window.location.href = authUrlWithParameters;
    },
    setTokenStatus(tokenStatus: BasespaceTokenStatusType | null) {
      this.tokenStatus = tokenStatus;
    },
    clearProjects() {
      this.nodes.clear();
      this.projectsLoaded = 0;
      this.projectsTotal = undefined;
    },
    upsert(nodes: BsNode[]) {
      nodes.forEach((n) => this.nodes.set(n.id, n));
    },
    /**
     * Toggle the checked state of a project, sample or
     * file node and propagate the change to its children and parents
     * considering the tri-state checkbox logic.
     */
    toggle(id: string) {
      // identify the node and toggle its state
      console.debug(`Toggling node with id: ${id}`);
      const node = this.nodes.get(id);
      if (!node) {
        console.warn(`Node ${id} not found (basespace.ts:toggle)`);
        return;
      }
      node.checked = !node.checked;

      // fetch children if node checked but children not fetched
      if (
        node.kind === 'project' &&
        node.checked &&
        (node.childrenLoaded === undefined || node.childrenTotal === undefined)
      ) {
        console.debug(
          `Node ${node.kind} ${node.name} (${node.id}) is checked, fetching children...`
        );
        this.fetchAllChildren(node);
      }

      // propagate down
      const setChildren = (newState: boolean | undefined, parentId: string) => {
        const node = this.nodes.get(parentId);
        if (!node) {
          console.warn(
            `Parent node ${parentId} not found (basespace.ts:toggle)`
          );
          return;
        }
        console.debug(
          `Setting children of ${node.kind} ${node.name} (${node.id}) to ${newState}, children:`,
          node.children
        );
        // if the node is
        (node.children || []).forEach((childId) => {
          const child = this.nodes.get(childId);
          if (!child) {
            console.warn(
              `Child node ${childId} not found (basespace.ts:toggle)`
            );
            return;
          }
          child.checked = newState;
          setChildren(newState, childId);
        });
      };
      setChildren(node.checked, id);

      // propagate up
      const walkUp = (childId: string) => {
        const child = this.nodes.get(childId);
        if (!child) {
          console.warn(
            `Child node with id ${childId} not found (basespace.ts:toggle)`
          );
          return;
        }
        if (!child.parentId) return;
        const parent = this.nodes.get(child.parentId);
        if (!parent) {
          console.warn(
            `Parent node with id ${child.parentId} not found (basespace.ts:toggle)`
          );
          return;
        }
        const statuses = (parent.children || [])
          .filter((cid) => !cid.startsWith('more'))
          .map((cid) => this.nodes.get(cid)?.checked);
        parent.checked = statuses.every((v) => v === true)
          ? true
          : statuses.every((v) => v === false)
          ? false
          : undefined;
        walkUp(parent.id);
      };
      walkUp(id);
    },

    /**
     * Fetch all projects from Basespace API.
     * Handles pagination and loading state.
     * If no projects are returned, it will log a warning.
     */
    async fetchAllProjects() {
      // Reset the state before fetching
      this.clearProjects();
      this.projectsLoading = true;

      // Fetch projects in pages
      while (
        this.projectsLoaded <
        (this.projectsTotal !== undefined ? this.projectsTotal : 1)
      ) {
        await this.fetchProjectsPage(this.projectsLoaded);
      }

      // Stop loading state
      this.projectsLoading = false;
    },

    /**
     * Fetch one page of projects from Basespace
     * API based on the provided offset.
     * Handles pagination and loading state.
     * If no projects are returned, it will log a warning.
     */
    async fetchProjectsPage(offset = 0) {
      const { data } = await apolloClient.query({
        query: BasespaceProjectsDocument,
        variables: {
          pagingOffset: offset,
          pagingLimit: PAGING_ITEMS_PER_PAGE,
        },
        fetchPolicy: 'network-only', // Always fetch fresh data
      });
      if (
        data.basespaceProjects?.projects === undefined ||
        data.basespaceProjects?.projects === null ||
        data.basespaceProjects?.total === undefined ||
        data.basespaceProjects?.total === null
      ) {
        console.warn('No data returned from BasespaceProjects query');
        return;
      }
      const newNodes = data.basespaceProjects.projects
        ?.filter((p) => p !== null && p.id !== null && p.name !== null)
        .map(
          (p) =>
            <BsNode>{
              id: p?.id,
              kind: 'project',
              data: p,
              name: p?.name,
              checked: false,
            }
        );

      this.projectsLoaded = offset + newNodes.length;
      this.projectsTotal = data.basespaceProjects?.total;
      this.upsert(newNodes);
    },

    /**
     * Clear all children of a given parent node (project or sample).
     * This is useful for resetting the state of the children
     * when the parent node is collapsed or when re-fetching.
     */
    clearChildren(parent: BsNode) {
      if (!parent) {
        console.warn('No parent node provided to clearChildren');
        return;
      }
      // Remove all children nodes from the store
      if (parent.children) {
        parent.children.forEach((childId) => {
          if (this.nodes.has(childId)) {
            this.nodes.delete(childId);
          }
        });
      }
      // Reset the parent node's state
      parent.children = [];
      parent.childrenLoaded = 0;
      parent.childrenTotal = undefined;
    },

    /**
     * Fetch all children of a given parent node.
     * If the parent is a project, fetches samples.
     * If the parent is a sample, fetches files.
     * Handles pagination and loading state.
     */
    async fetchAllChildren(parent: BsNode) {
      // Clear existing children before fetching
      this.clearChildren(parent);
      parent.children = [];
      parent.childrenLoaded = 0;
      parent.childrenLoading = true;

      // Fetch children in pages
      while (
        parent.childrenLoaded <
        (parent.childrenTotal !== undefined ? parent.childrenTotal : 1)
      ) {
        console.debug(
          `Fetching children for ${parent.kind} ${parent.id}, loaded: ${parent.childrenLoaded}, total: ${parent.childrenTotal}`
        );
        await this.fetchChildrenPage(parent, parent.childrenLoaded);
      }

      // Fetch children of all children nodes
      for (const childId of parent.children) {
        const child = this.nodes.get(childId);
        if (child && (child.kind === 'project' || child.kind === 'sample')) {
          // Fetch children for each project or sample
          if (
            child.childrenLoaded === undefined ||
            child.childrenTotal === undefined ||
            child.childrenLoaded < child.childrenTotal
          ) {
            await this.fetchAllChildren(child);
          }
        }
      }

      // Stop loading state
      parent.childrenLoading = false;
    },

    /**
     * Fetch page of children nodes for the given parent node (project or sample) based on the offset.
     * If the parent is a project, fetches samples.
     * If the parent is a sample, fetches files.
     * Handles pagination and loading state.
     */
    async fetchChildrenPage(parent: BsNode, offset = 0) {
      const childrenType = parent.kind === 'project' ? 'sample' : 'file';
      const childrenLoaded = parent.childrenLoaded ? parent.childrenLoaded : 0;
      let childrenData:
        | (BasespaceSampleType | null)[]
        | (BasespaceFileType | null)[]
        | null
        | undefined = null;
      let childrenTotal: number | null | undefined = null;

      // Fetch sample if parent is a project
      if (childrenType === 'sample') {
        const { data } = await apolloClient.query({
          query: BasespaceProjectSamplesDocument,
          variables: {
            projectId: parent.id,
            pagingOffset: offset,
            pagingLimit: PAGING_ITEMS_PER_PAGE,
          },
          fetchPolicy: 'network-only', // Always fetch fresh data
        });
        childrenData = data.basespaceProjectSamples?.samples;
        childrenTotal = data.basespaceProjectSamples?.total;
      }
      // Fetch files if parent is a sample
      else if (childrenType === 'file') {
        const { data } = await apolloClient.query({
          query: BasespaceSampleFilesDocument,
          variables: {
            sampleId: parent.id,
            pagingOffset: childrenLoaded,
            pagingLimit: PAGING_ITEMS_PER_PAGE,
          },
          fetchPolicy: 'network-only', // Always fetch fresh data
        });
        childrenData = data.basespaceSampleFiles?.files;
        childrenTotal = data.basespaceSampleFiles?.total;
      }

      // Validate new nodes were obtained
      if (childrenData === null || childrenData === undefined) {
        throw new Error(
          `Error obtaining ${childrenType}s for ${parent.kind} ${parent.id}`
        );
      }

      // Validate total number of children was obtained
      if (childrenTotal === undefined || childrenTotal === null) {
        throw new Error(
          `Total number of ${childrenType}s is not defined for ${parent.kind} ${parent.id}`
        );
      }

      // Catalog the newly obtained children nodes
      const newNodes = childrenData.map(
        (child) =>
          <BsNode>{
            id: child?.id,
            kind: childrenType,
            data: child,
            parentId: parent.id,
            name: child?.name,
            children: [],
            checked: parent.checked,
          }
      );

      parent.childrenTotal = childrenTotal;
      parent.childrenLoaded = childrenLoaded + newNodes.length;

      // Update parent children and upsert new nodes
      parent.children = (parent.children || []).concat(
        newNodes.map((n) => n.id)
      );
      this.upsert(newNodes);
    },
  },
});
